import {
  createFileRoute,
  Outlet,
  useMatches,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useGetSpaceInformation } from "@/api/generated/space-스페이스";
import type { ApiResponseSpaceResponse, SpaceResponse } from "@/api/model";
import QrBottomSheetContent from "@/components/UI/QrBottomSheetContent/QrBottomSheetContent";
import ShareModal from "@/components/UI/ShareModal/ShareModal";
import useFlowBack from "@/hooks/@common/useFlowBack";
import useKakaoShareBridge from "@/hooks/@common/useKakaoShareBridge";
import useSnackBar from "@/hooks/@common/useSnackBar";
import SpaceLayout from "@/pages/space/SpaceLayout";
import { trackEvent } from "@/utils/analytics";

export const Route = createFileRoute(
  "/_appOnly/_authenticated/spaces/$spaceId",
)({
  component: RouteComponent,
});

// TODO: 리팩토링 필요 — 공유 모달/QR 시트 상태(isShareModalOpen, isQrSheetOpen)와
// onKakaoShare/onCopyLink/onSaveQr 핸들러, 관련 트래킹 호출이 라우트 파일에 직접 들어
// 있어 책임이 과도하게 몰려 있다. useSpaceShare 같은 커스텀 훅이나 SpaceShareSection
// 컴포넌트로 추출해 라우트는 얇게 유지하는 방향 검토
function RouteComponent() {
  const { spaceId } = Route.useParams();
  const navigate = useNavigate();
  const flowBack = useFlowBack();
  const { showSnackBar } = useSnackBar();
  const { requestKakaoShare } = useKakaoShareBridge();
  const { data: space } = useGetSpaceInformation<SpaceResponse>(spaceId, {
    query: {
      select: (response) =>
        (response as unknown as ApiResponseSpaceResponse).data ?? {},
    },
  });
  const matches = useMatches();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isQrSheetOpen, setIsQrSheetOpen] = useState(false);

  useEffect(() => {
    if (!space?.name) return;
    trackEvent("space_viewed", {
      space_id: spaceId,
      space_name: space.name,
      viewer_role: "host",
    });
  }, [spaceId, space?.name]);
  const isGuestBookTab = matches.some(
    (match) =>
      match.routeId === "/_appOnly/_authenticated/spaces/$spaceId/guestbook/",
  );
  const isDetailRoute = matches.some(
    (match) =>
      match.routeId.startsWith(
        "/_appOnly/_authenticated/spaces/$spaceId/artworks/$artworkId",
      ) ||
      match.routeId ===
        "/_appOnly/_authenticated/spaces/$spaceId/guestbook/$guestbookId" ||
      match.routeId ===
        "/_appOnly/_authenticated/spaces/$spaceId/create-product/" ||
      match.routeId === "/_appOnly/_authenticated/spaces/$spaceId/edit/",
  );

  if (isDetailRoute) {
    return <Outlet />;
  }

  const writeUrl = `${import.meta.env.VITE_DOMAIN}/spaces/${spaceId}/guest`;

  return (
    <>
      <SpaceLayout
        activeTab={isGuestBookTab ? "right" : "left"}
        onBack={() => flowBack({ to: "/home" })}
        onArtworkTabClick={() =>
          navigate({
            to: "/spaces/$spaceId",
            params: { spaceId },
            replace: true,
          })
        }
        onGuestBookTabClick={() =>
          navigate({
            to: "/spaces/$spaceId/guestbook",
            params: { spaceId },
            replace: true,
          })
        }
        onShareClick={() => setIsShareModalOpen(true)}
      >
        <Outlet />
      </SpaceLayout>

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        onKakaoShare={() => {
          requestKakaoShare({
            title: `${space?.name ?? ""} | Forgather`,
            description: "스페이스에 초대되었습니다",
            link: writeUrl,
            buttonTitle: "방명록 남기기",
          });
          trackEvent("space_shared", {
            space_id: spaceId,
            share_channel: "kakao",
          });
          setIsShareModalOpen(false);
        }}
        onCopyLink={async () => {
          await navigator.clipboard.writeText(writeUrl);
          trackEvent("space_shared", {
            space_id: spaceId,
            share_channel: "link",
          });
          showSnackBar("링크가 클립보드에 복사되었습니다.", "default");
          setIsShareModalOpen(false);
        }}
        onSaveQr={() => {
          setIsShareModalOpen(false);
          setIsQrSheetOpen(true);
        }}
      />

      {isQrSheetOpen && (
        <QrBottomSheetContent
          isOpen={isQrSheetOpen}
          onClose={() => setIsQrSheetOpen(false)}
          qrValue={writeUrl}
          onSaveSuccess={() =>
            trackEvent("space_qr_saved", { space_id: spaceId })
          }
        />
      )}
    </>
  );
}
